import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

/** Commit the route before doing the portfolio's first render. Otherwise the
 * outgoing dashboard's live store updates can repeatedly interrupt that render
 * inside the navigation transition. Its subscriptions must be released first.
 *
 * This boundary runs once per route entry. It never delays account/feed events,
 * changes their cadence, or remounts an already-open tracker on an update. */
export function PositionTrackerEntry({ children }: { children: ReactNode }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    // Let the committed loading view reach a frame, and cancel the mount if
    // another navigation wins first. No portfolio work runs for that abandoned
    // visit. A hidden tab mounts on its next visible frame.
    const frame = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  if (!entered) {
    return (
      <div role="status" className="text-muted-foreground flex h-full items-center justify-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
        Loading Position Tracker…
      </div>
    );
  }
  return children;
}
